import { Component } from '@theme/component';

/**
 * @typedef {Object} CollapseRefs
 * @property {HTMLElement} [button] - The optional button element to trigger collapse/expand
 * @property {HTMLElement} panel - The panel element to collapse/expand
 */

/**
 * A custom element that manages collapse/expand functionality
 *
 * @extends {Component<CollapseRefs>}
 */
class CollapseCustom extends Component {
  requiredRefs = ['panel'];

  /**
   * @type {boolean}
   */
  #expanded = false;

  /**
   * @type {number}
   */
  #collapsedHeight = 0;

  /**
   * @type {number}
   */
  #collapsedHeightThreshold = 0;

  /**
   * @type {number}
   */
  #fullHeight = 0;

  /**
   * @type {Animation | undefined}
   */
  #animation;


  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  /**
   * @type {AbortController}
   */
  #controller = new AbortController();

  /**
   * @type {HTMLElement | null}
   */
  #buttonElement = null;

  /**
   * @type {string}
   */
  #initialButtonText = '';

  /**
   * @type {string}
   */
  #expandText = '';

  /**
   * @type {string}
   */
  #collapseText = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.#expanded = this.hasAttribute('expanded');

    const collapsedHeightAttr = this.getAttribute('collapsed-height');
    if (collapsedHeightAttr) {
      const collapsedHeight = parseInt(collapsedHeightAttr, 10);
      if (!Number.isNaN(collapsedHeight)) {
        this.#collapsedHeight = Math.max(collapsedHeight, 0);
        this.#collapsedHeightThreshold = Math.max(collapsedHeight - 8, 0);
      }
    }
    
    if (this.#collapsedHeight === 0) {
      this.#collapsedHeight = Math.max(this.refs.panel.offsetHeight || 0, 0);
      this.#collapsedHeightThreshold = Math.max(this.#collapsedHeight - 8, 0);
    }
    
    if (this.refs.button) {
      this.refs.button.addEventListener('click', this.#handleButtonClick, { signal });
      this.refs.button.setAttribute('aria-expanded', String(this.#expanded));
      
      this.#initializeButtonText();
    }


    if (!this.#expanded) {
      const { panel } = this.refs;
      panel.style.height = 'auto';
      this.#fullHeight = panel.scrollHeight;
      panel.style.height = `${this.#collapsedHeight}px`;
      panel.style.overflow = 'hidden';
    } else {
      const { panel } = this.refs;
      panel.style.height = 'auto';
      this.#fullHeight = panel.scrollHeight;
      panel.style.height = '';
    }

    if (this.refs.panel.classList.contains('collapsible__panel--fade')) {
      const fadeHeight = this.refs.panel.getAttribute('data-fade-height');
      const fadeSize = fadeHeight ? parseInt(fadeHeight, 10) : 60;
      
      if (!this.#expanded) {
        this.refs.panel.style.setProperty('--fade-size', `${fadeSize}px`);
      } else {
        this.refs.panel.style.setProperty('--fade-size', '0px');
      }
    }

    this.#updateButtonVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles button click to toggle collapse/expand
   * @param {Event} event
   */
  #handleButtonClick = (event) => {
    event.preventDefault();
    this.toggle();
  };

  /**
   * Collapses the panel
   */
  collapse() {
    if (!this.#expanded) return;

    this.#dispatchEvent('onCollapse');

    const { panel } = this.refs;
    const startHeight = panel.offsetHeight;
    const endHeight = this.#collapsedHeight;

    this.#animation?.cancel();

    this.#animation = panel.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    if (this.refs.panel.classList.contains('collapsible__panel--fade')) {
      const fadeHeight = this.refs.panel.getAttribute('data-fade-height');
      const fadeSize = fadeHeight ? parseInt(fadeHeight, 10) : 60;
      this.refs.panel.style.setProperty('--fade-size', `${fadeSize}px`);
    }

    this.#animation.onfinish = () => {
      panel.style.height = `${this.#collapsedHeight}px`;
      panel.style.overflow = 'hidden';
      this.#expanded = false;
      this.removeAttribute('expanded');
      if (this.refs.button) {
        this.refs.button.setAttribute('aria-expanded', 'false');
      }
      this.#updateButtonText();
      this.#updateButtonVisibility();
      this.#dispatchEvent('onCollapseEnd');
    };
  }

  /**
   * Expands the panel
   */
  expand() {
    if (this.#expanded) return;

    this.#dispatchEvent('onExpanding');

    const { panel } = this.refs;
    const startHeight = panel.offsetHeight;
    
    panel.style.height = 'auto';
    const endHeight = panel.scrollHeight;
    panel.style.height = `${startHeight}px`;
    panel.style.overflow = 'hidden';

    this.#animation?.cancel();

    this.#animation = panel.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    if (this.refs.panel.classList.contains('collapsible__panel--fade')) {
      this.refs.panel.style.setProperty('--fade-size', '0px');
    }

    this.#animation.onfinish = () => {
      panel.style.removeProperty('height');
      panel.style.overflow = '';
      this.#expanded = true;
      this.setAttribute('expanded', '');
      this.#fullHeight = panel.scrollHeight;
      if (this.refs.button) {
        this.refs.button.setAttribute('aria-expanded', 'true');
      }
      this.#updateButtonText();
      this.#updateButtonVisibility();
      this.#dispatchEvent('onExpandEnd');
    };
  }

  /**
   * Toggles between collapsed and expanded states
   */
  toggle() {
    if (this.#expanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Initializes button text based on data attributes and initial state
   */
  #initializeButtonText() {
    if (!this.refs.button) return;

    const foundElement = this.refs.button.querySelector('a, button');
    const buttonElement = (foundElement instanceof HTMLElement ? foundElement : this.refs.button);
    this.#buttonElement = buttonElement;

    this.#initialButtonText = buttonElement.textContent?.trim() || '';

    const expandTextAttr = this.refs.button.getAttribute('data-expand-text');
    const collapseTextAttr = this.refs.button.getAttribute('data-collapse-text');

    if (this.#expanded) {
      if (collapseTextAttr) {
        this.#collapseText = collapseTextAttr;
      } else {
        this.#collapseText = this.#initialButtonText;
        this.refs.button.setAttribute('data-collapse-text', this.#initialButtonText);
      }
      this.#expandText = expandTextAttr || '';
    } else {
      if (expandTextAttr) {
        this.#expandText = expandTextAttr;
      } else {
        this.#expandText = this.#initialButtonText;
        this.refs.button.setAttribute('data-expand-text', this.#initialButtonText);
      }
      this.#collapseText = collapseTextAttr || '';
    }

    this.#updateButtonText();
  }

  /**
   * Updates button text based on current state
   */
  #updateButtonText() {
    if (!this.#buttonElement) return;

    const text = this.#expanded ? this.#collapseText : this.#expandText;
    if (text) {
      this.#buttonElement.textContent = text;
    }
  }

  /**
   * Updates button visibility based on panel height
   */
  #updateButtonVisibility() {
    if (this.#collapsedHeightThreshold === 0) return;

    const { panel } = this.refs;
    let fullHeight = this.#fullHeight;
    
    if (fullHeight === 0) {
      const tempHeight = panel.style.height;
      panel.style.height = 'auto';
      fullHeight = panel.scrollHeight;
      panel.style.height = tempHeight;
      this.#fullHeight = fullHeight;
    }

    const shouldHide = fullHeight < this.#collapsedHeightThreshold;

    if (this.refs.button) {
      if (shouldHide) {
        this.refs.button.style.display = 'none';
      } else {
        this.refs.button.style.display = '';
      }
    }

  }

  /**
   * Dispatches a custom event
   * @param {string} eventName
   */
  #dispatchEvent(eventName) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: {
        expanded: this.#expanded,
        element: this,
      },
    });
    this.dispatchEvent(event);
  }
}

if (!customElements.get('collapse-custom')) {
  customElements.define('collapse-custom', CollapseCustom);
}

